import React, { FC, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import type { CellProps } from "recharts";

import {
  Tabs,
  TabsList,
  TabsTrigger,
  Card,
  CardContent,
} from "@/components/ui";
import { Props, ChartDataItem } from "./types";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/Chart";
import { ChartHeader } from "@/components/ChartHeader";

export const HorizontalStackedBarChart: FC<Props> = ({
  title,
  subtitle,
  data,
  chartConfig,
  seriesConfig,
  axisConfig,
  tabsConfig,
  stateKey,
  onSegmentClick,
  menuOptions,
  footerContent,
  height = 300,
  margin = { top: 0, right: 40, left: 10, bottom: 0 },
}) => {
  const defaultTab =
    tabsConfig?.[0]?.value || (Object.keys(data)[0] as keyof typeof data);

  const [tabStates, setTabStates] = useState<Record<string, keyof typeof data>>(
    {},
  );

  const activeTab =
    stateKey && tabStates[stateKey]
      ? tabStates[stateKey]
      : stateKey
        ? defaultTab
        : tabStates["default"] || defaultTab;

  const handleTabChange = (val: keyof typeof data) => {
    if (stateKey) {
      setTabStates((prev) => ({ ...prev, [stateKey]: val }));
    } else {
      setTabStates((prev) => ({ ...prev, default: val }));
    }
  };

  const handleBarClick = (
    cellData: CellProps & { payload: ChartDataItem },
    index: number,
    seriesKey: string,
  ) => {
    if (!onSegmentClick) return;

    const categoryValue = cellData.payload[axisConfig.categoryKey];
    onSegmentClick(
      String(categoryValue),
      seriesKey,
      activeTab,
      cellData.payload,
    );
  };

  const currentData = data[activeTab] || [];

  return (
    <Card data-testid="horizontal-card-wrapper">
      <ChartHeader {...{ title, subtitle, menuOptions }} />
      <CardContent className={footerContent ? "pb-0" : undefined}>
        <div className="mb-6 flex flex-col-reverse items-start justify-between gap-2 sm:flex-row sm:items-center">
          {tabsConfig && tabsConfig.length > 1 ? (
            <Tabs
              value={activeTab as string}
              onValueChange={(val) => handleTabChange(val as keyof typeof data)}
            >
              <TabsList className="gap-2">
                {tabsConfig.map((tabConfig) => (
                  <TabsTrigger key={tabConfig.value} value={tabConfig.value}>
                    {tabConfig.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          ) : (
            <div aria-hidden="true" />
          )}

          <div className="flex items-center gap-4 text-sm">
            {seriesConfig.map(({ key, label, color }) => (
              <div key={key} className="flex items-center gap-1">
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{
                    backgroundColor: color,
                    width: "20px",
                    height: "5px",
                  }}
                />
                <span className="text-muted-foreground">{label}</span>
              </div>
            ))}
          </div>
        </div>

        <ResponsiveContainer width="100%" height={height}>
          <ChartContainer
            config={chartConfig}
            className="relative mt-4 w-full"
            style={{ height }}
          >
            <BarChart
              layout="vertical"
              data={currentData}
              accessibilityLayer
              barCategoryGap={10}
              margin={margin}
            >
              <CartesianGrid
                stroke="var(--cartesian-1)"
                strokeDasharray="0"
                vertical={true}
                horizontal={false}
              />
              <XAxis
                axisLine={false}
                type="number"
                tickMargin={10}
                tickLine={false}
                label={
                  axisConfig.valueLabel
                    ? {
                        value: axisConfig.valueLabel,
                        position: "insideBottom",
                        offset: -10,
                      }
                    : undefined
                }
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                dataKey={axisConfig.categoryKey}
                type="category"
                width={75}
                label={
                  axisConfig.categoryLabel
                    ? {
                        value: axisConfig.categoryLabel,
                        angle: -90,
                        position: "insideLeft",
                      }
                    : undefined
                }
              />
              <ChartTooltip
                content={<ChartTooltipContent className="w-[250px]" />}
              />

              {seriesConfig.map((series, index) => {
                const isLast = index === seriesConfig.length - 1;
                return (
                  <Bar
                    key={series.key}
                    dataKey={series.key}
                    stackId="a"
                    className="hover:cursor-pointer"
                    radius={isLast ? [0, 5, 5, 0] : [0, 0, 0, 0]}
                    fill={series.color}
                    minPointSize={1}
                    onClick={(d, i) => handleBarClick(d, i, series.key)}
                  />
                );
              })}
            </BarChart>
          </ChartContainer>
        </ResponsiveContainer>
      </CardContent>
      {footerContent && <div className="mt-6">{footerContent}</div>}
    </Card>
  );
};
